// Live filter thumbnails (§11.19): her photo with each filter, rendered at 160 px from the preview texture
// by the viewer's own renderer (no extra decode). Re-rendered when the photo or its non-filter look changes.
// M18: round plate thumbnails, the selected one ringed in green; before a photo is open, sample thumbnails.
import { useEffect, useRef, useState } from 'react';
import { viewerHooks } from '../canvas/Viewer';
import { useEditor } from '../../state/editor';
import { useLibrary } from '../../state/library';
import { useSimple } from '../../state/simple';
import { useLang, useT, type MessageKey } from '../../i18n';
import {
  applyFilter,
  FILTER_GROUPS,
  FOOD_TABS,
  foodTab,
  SIMPLE_FILTERS,
  type FilterGroup,
  type FoodTab,
} from '../../features/simpleFilters';
import { chooseFilter, setFilterStrength } from '../../features/simpleActions';
import { fullOutputSize } from '../../engine/geometry';
import type { Settings } from '../../model/types';
import { defaultSettings } from '../../model/defaults';
import { openPhotoFiles } from './SimplePanel';
import f from './Filters.module.css';

const GROUP_LABEL: Record<FilterGroup, MessageKey> = {
  food: 'fgFood',
  everyday: 'fgEveryday',
  mood: 'fgMood',
  people: 'fgPeople',
  bw: 'fgBw',
};
/** Tabs inside Food: the original 10 food filters and the Mega Food Pack's sections. */
export const FOOD_TAB_LABEL: Record<FoodTab, MessageKey> = {
  originals: 'foodOriginals',
  fix: 'foodSecFix',
  dish: 'foodSecDish',
  style: 'foodSecStyle',
  menu: 'foodSecMenu',
};
export const THUMB = 160;
const DEFAULTS = defaultSettings();
/** Timing of the last full thumbnail render, for the §11.19 acceptance test and the benchmark. */
export const thumbStats = { lastMs: 0, count: 0 };

function renderThumbs(base: Settings, canvases: Map<string, HTMLCanvasElement>): boolean {
  const c = viewerHooks.controller;
  const src = c?.engine.source;
  if (!c || !src) return false;
  const t0 = performance.now();
  const full = fullOutputSize(base.geometry, src.width, src.height);
  const k = THUMB / Math.max(full.width, full.height);
  const w = Math.max(1, Math.round(full.width * k)),
    h = Math.max(1, Math.round(full.height * k));
  const out = {
    space: 'srgb' as const,
    flatten: true,
    fill: [0.43, 0.43, 0.43] as [number, number, number],
    clipping: false,
  };
  const px = new Uint8ClampedArray(w * h * 4);
  for (const fl of SIMPLE_FILTERS) {
    const cv = canvases.get(fl.id);
    if (!cv) continue;
    const s = applyFilter(base, fl, 1);
    const d = c.engine.readOutput(c.engine.renderPreview(s, w, h, undefined, { superset: true }), out);
    for (let i = 0; i < d.length; i += 4) {
      px[i] = d[i]! * 255;
      px[i + 1] = d[i + 1]! * 255;
      px[i + 2] = d[i + 2]! * 255;
      px[i + 3] = 255;
    }
    cv.width = w;
    cv.height = h;
    cv.getContext('2d')!.putImageData(new ImageData(px, w, h), 0, 0);
  }
  thumbStats.lastMs = performance.now() - t0;
  thumbStats.count = canvases.size;
  c.request(); // the viewer's targets were borrowed: redraw the main view
  return true;
}

type SampleMap = { cols: number; rows: number; ids: string[] };

/**
 * M18: before a photo is open, step 3 is never empty — the Food tabs with thumbnails of a bundled sample dish
 * (public/samples/filters.jpg, a sprite made by scripts/make-filter-samples.ts with the app's own renderer)
 * and a line saying to open a photo. Tapping a sample opens the photo picker.
 */
function SampleFilters() {
  const t = useT();
  const lang = useLang((s) => s.lang);
  const [sub, setSub] = useState<FoodTab>('originals');
  const [map, setMap] = useState<SampleMap | null>(null);
  useEffect(() => {
    let alive = true;
    fetch('/samples/filters.json')
      .then((r) => (r.ok ? (r.json() as Promise<SampleMap>) : null))
      .then((m) => alive && setMap(m))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  const food = SIMPLE_FILTERS.filter((x) => x.group === 'food' && foodTab(x) === sub);
  const cell = (id: string) => {
    const i = map ? map.ids.indexOf(id) : -1;
    if (!map || i < 0) return undefined;
    const col = i % map.cols,
      row = Math.floor(i / map.cols);
    return {
      backgroundImage: 'url(/samples/filters.jpg)',
      backgroundSize: `${map.cols * 100}% ${map.rows * 100}%`,
      backgroundPosition: `${(col * 100) / Math.max(1, map.cols - 1)}% ${(row * 100) / Math.max(1, map.rows - 1)}%`,
    };
  };
  return (
    <div data-testid="filter-samples">
      <p className={f.hint} data-testid="filter-samples-hint">
        {t('filtersSampleHint')}
      </p>
      <div role="tablist" className={f.tabs}>
        <span className={f.groupOn} data-testid="filter-group-food">
          {t('fgFood')}
        </span>
      </div>
      <div role="tablist" className={f.subtabs}>
        {FOOD_TABS.map((ft) => (
          <button
            key={ft}
            role="tab"
            className={f.sub}
            data-testid={`food-tab-${ft}`}
            aria-selected={sub === ft}
            onClick={() => setSub(ft)}
          >
            {t(FOOD_TAB_LABEL[ft])}
          </button>
        ))}
      </div>
      <div className={f.grid}>
        {food.map((x) => (
          <button
            key={x.id}
            className={f.tile}
            data-testid={`sample-${x.id}`}
            onClick={() => void openPhotoFiles()}
          >
            <span className={f.plate} style={cell(x.id)} />
            <span className={f.name}>{lang === 'es' ? x.nameEs : x.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function FilterGrid() {
  const id = useLibrary((s) => s.activeId);
  return id ? <LiveFilterGrid /> : <SampleFilters />;
}

function LiveFilterGrid() {
  const t = useT();
  const lang = useLang((s) => s.lang);
  const id = useLibrary((s) => s.activeId);
  // An unedited photo has no entry yet: fall back to the defaults (a stable object).
  const settings = useEditor((s) => (id ? s.settings[id] : undefined)) ?? (id ? DEFAULTS : undefined);
  const rec = useSimple((s) => (id ? s.filter[id] : undefined));
  const [group, setGroup] = useState<FilterGroup>('food');
  const [sub, setSub] = useState<FoodTab>('originals');
  const canvases = useRef(new Map<string, HTMLCanvasElement>());
  const [ready, setReady] = useState(0);
  const layoutReady = !!viewerHooks.controller?.layout;

  // The look the thumbnails start from: without the filter that is currently applied.
  const base: Settings | undefined =
    rec && settings && JSON.stringify(rec.applied) === JSON.stringify(settings) ? rec.base : settings;
  const baseKey = JSON.stringify(base ?? null);
  useEffect(() => {
    if (!base) return;
    const timer = window.setTimeout(() => {
      if (!renderThumbs(base, canvases.current)) setReady((n) => n + 1); // retry until the viewer is ready
    }, 120);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, baseKey, group, ready, layoutReady]);

  // Compile the thumbnail shader while the user is still looking at the photo (one program for all 33).
  useEffect(() => {
    if (layoutReady) viewerHooks.controller?.engine.prewarmSuperset();
  }, [layoutReady]);

  const strength = rec ? Math.round(rec.strength * 100) : 100;
  return (
    <div data-testid="filter-grid">
      <div role="tablist" className={f.tabs}>
        {FILTER_GROUPS.map((g) => (
          <button
            key={g}
            role="tab"
            className={f.group}
            aria-selected={group === g}
            onClick={() => setGroup(g)}
          >
            {t(GROUP_LABEL[g])}
          </button>
        ))}
      </div>
      {group === 'food' && (
        <div role="tablist" className={f.subtabs}>
          {FOOD_TABS.map((ft) => (
            <button
              key={ft}
              role="tab"
              className={f.sub}
              data-testid={`food-tab-${ft}`}
              aria-selected={sub === ft}
              onClick={() => setSub(ft)}
            >
              {t(FOOD_TAB_LABEL[ft])}
            </button>
          ))}
        </div>
      )}
      <div className={f.grid}>
        <button
          data-testid="filter-none"
          className={f.tile}
          onClick={() => chooseFilter(null)}
          aria-pressed={!rec}
        >
          <span className={`${f.plate} ${f.none}`} />
          <span className={f.name}>{t('noFilter')}</span>
        </button>
        {SIMPLE_FILTERS.map((fl) => (
          <button
            key={fl.id}
            data-testid={`filter-${fl.id}`}
            aria-pressed={rec?.id === fl.id}
            className={f.tile}
            onClick={() => chooseFilter(fl.id, rec ? rec.strength : 1)}
            style={{
              display: fl.group === group && (group !== 'food' || foodTab(fl) === sub) ? undefined : 'none',
            }}
          >
            <canvas
              className={f.plate}
              ref={(el) => {
                if (el) canvases.current.set(fl.id, el);
                else canvases.current.delete(fl.id);
              }}
            />
            <span className={f.name}>{lang === 'es' ? fl.nameEs : fl.name}</span>
          </button>
        ))}
      </div>
      {rec && (
        <label className={f.strength}>
          {t('strength')} {strength}%
          <input
            type="range"
            min={0}
            max={100}
            value={strength}
            data-testid="filter-strength"
            onChange={(e) => setFilterStrength(Number(e.target.value) / 100, false)}
            onPointerUp={(e) => setFilterStrength(Number((e.target as HTMLInputElement).value) / 100, true)}
            onKeyUp={(e) => setFilterStrength(Number((e.target as HTMLInputElement).value) / 100, true)}
          />
        </label>
      )}
    </div>
  );
}
